import { createClient } from '@supabase/supabase-js';
import { isSSR } from '../isSSR/index.js';

// Supabase URL and anon key - from .env file or hardcoded fallbacks
const supabaseUrl = 'https://qldjmxfimvxskdbastkx.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFsZGpteGZpbXZ4c2tkYmFzdGt4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzk3Mjk5ODUsImV4cCI6MjA1NTMwNTk4NX0.yXHezaqqVqD6tK9IhkJ-Lw6FaE1mhjt-hNCsu7Tvf-w';

// Create custom storage mechanism for SSR compatibility
const customStorage = {
  getItem: (key) => {
    try {
      if (isSSR()) {
        console.log('[Supabase Custom Role] Skipping localStorage.getItem in SSR mode');
        return null;
      }
      return localStorage.getItem(key);
    } catch (e) {
      console.warn('[Supabase Custom Role] Storage getItem error:', e);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      if (isSSR()) {
        console.log('[Supabase Custom Role] Skipping localStorage.setItem in SSR mode');
        return;
      }
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn('[Supabase Custom Role] Storage setItem error:', e);
    }
  },
  removeItem: (key) => {
    try {
      if (isSSR()) {
        console.log('[Supabase Custom Role] Skipping localStorage.removeItem in SSR mode');
        return;
      }
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('[Supabase Custom Role] Storage removeItem error:', e);
    }
  },
};

// Create a Supabase client with the custom role
export const supabaseCustomRole = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    storageKey: 'gildy_auth_role_token',
    storage: customStorage,
  },
  global: {
    headers: {
      'x-gildy-client': 'web-app',
      'apikey': supabaseAnonKey,
      'Content-Type': 'application/json'
    },
    fetch: (url, fetchOptions) => {
      // Fix for the "public.public." issue - remove the double prefix
      let modifiedUrl = url;
      if (url.includes('/rest/v1/public.')) {
        modifiedUrl = url.replace('/rest/v1/public.', '/rest/v1/');
        console.log('[Supabase Custom Role] Removing public. prefix from URL:', url, '->', modifiedUrl);
      }

      // Add custom headers for role client
      const headers = new Headers(fetchOptions.headers);
      headers.set('x-gildy-client', 'web-app');
      headers.set('apikey', supabaseAnonKey);
      headers.set('Content-Type', 'application/json');
      
      // Add schema header - THIS IS THE KEY FIX
      headers.set('Accept-Profile', 'public');
      headers.set('Content-Profile', 'public');
      
      const newOptions = {
        ...fetchOptions,
        headers
      };

      // Track which tables we're accessing for debug purposes
      const tableName = extractTableNameFromUrl(modifiedUrl);
      if (tableName) {
        console.log(`[Supabase Custom Role] Accessing table: ${tableName}`);
      }

      return fetch(modifiedUrl, newOptions)
        .then(response => {
          if (!response.ok) {
            const status = response.status;
            const statusText = response.statusText;
            
            if (status === 404 && tableName) {
              console.error(`[Supabase Custom Role] Table not found or not accessible: ${tableName} (${status} ${statusText})`);
              
              // If we're trying to access the favorites table, provide a more helpful error message
              if (tableName === 'favorites') {
                console.warn('[Supabase Custom Role] Favorites table issue detected. Please run the fix-favorites-permissions script.');
              }
            } else if (status === 406 && tableName) {
              console.error(`[Supabase Custom Role] Schema issue with table: ${tableName} (${status} ${statusText})`);
              
              if (tableName === 'favorites') {
                console.warn('[Supabase Custom Role] Favorites table schema issue detected. Please run the fix-favorites-permissions script.');
              }
            } else if (status === 401 && tableName) {
              console.error(`[Supabase Custom Role] Authentication issue with table: ${tableName} (${status} ${statusText})`);
              
              if (tableName === 'favorites') {
                console.warn('[Supabase Custom Role] RLS policy issue detected for favorites table. Please check your authentication status and/or run the fix-favorites-permissions script.');
              }
            } else {
              console.error(`[Supabase Custom Role] Resource not found: ${modifiedUrl} (${status} ${statusText})`);
            }
          }
          return response;
        })
        .catch(error => {
          console.error('[Supabase Custom Role] Fetch error:', error, modifiedUrl);
          return new Response(JSON.stringify({
            error: 'Network error',
            message: 'Failed to connect to Supabase API',
            code: 'NETWORK_ERROR'
          }), { status: 503 });
        });
    }
  },
  db: {
    schema: 'public' // Explicitly set the schema to public
  }
});

// Helper function to extract table name from a URL
function extractTableNameFromUrl(url) {
  try {
    // Extract table name from URL patterns like /rest/v1/tablename or /rest/v1/tablename?query
    const match = url.match(/\/rest\/v1\/([^?]+)/);
    if (match && match[1]) {
      return match[1];
    }
    return null;
  } catch (e) {
    console.warn('[Supabase Custom Role] Error extracting table name:', e);
    return null;
  }
}

// Function to check if a table exists
export const checkTableExists = async (tableName) => {
  if (!tableName) return false;
  
  try {
    const { error } = await supabaseCustomRole
      .from(tableName)
      .select('count')
      .limit(0);
    
    if (!error) return true;
    
    if (error.code === '42P01' || error.message.includes('does not exist')) {
      console.log(`[Supabase Custom Role] Table ${tableName} doesn't exist`);
      return false;
    }
    
    console.error(`[Supabase Custom Role] Error checking table ${tableName}:`, error);
    return false;
  } catch (err) {
    console.error(`[Supabase Custom Role] Exception checking table ${tableName}:`, err);
    return false;
  }
};

// Export helper functions
export const checkCustomRoleClient = () => {
  console.log('[Supabase Custom Role] Client status check');
  return true;
};
